import React, { useMemo } from 'react'
import { useApolloClient, ApolloClient, NormalizedCacheObject } from '@apollo/client'
import classNames from 'classnames'
import { format } from 'date-fns'
import { ExternalLink } from '@/atoms/ExternalLink'
import { TitleXXS, CaptionMD, CaptionSM, AsSpan } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { useAngxStockSnapshot } from '@/services/StockService'
import { useSafeTrack } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'

/**
 * ANGXStockTicker
 * Displays the latest ANGX stock price, absolute change, and percent change along with the last updated timestamp.
 * Data refreshes every 15 minutes via Apollo polling configured in the hook.
 */
export const ANGXStockTicker: React.FC<{ className?: string }> = ({ className }) => {
  const { t } = useTranslate('common')
  const apolloClient = useApolloClient() as ApolloClient<NormalizedCacheObject>
  const { stockSnapshot, loading } = useAngxStockSnapshot(apolloClient)
  const track = useSafeTrack()

  const { priceDisplay, changeDisplay, percentDisplay, timestampDisplay } = useMemo(() => {
    if (!stockSnapshot) {
      return {
        priceDisplay: loading ? '—' : t('notAvailable', 'N/A'),
        changeDisplay: '',
        percentDisplay: '',
        timestampDisplay: '',
      }
    }

    const { price, changePercent, timestamp } = stockSnapshot
    const numericChangePercent = changePercent ?? 0
    // Compute absolute change from percent.
    const absoluteChange = price !== null && price !== undefined ? (price * numericChangePercent) / 100 : undefined
    const isPositive = (absoluteChange ?? 0) >= 0

    const formatter = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })

    const priceDisplay = price !== null && price !== undefined ? formatter.format(price) : t('notAvailable', 'N/A')

    const changeDisplay =
      absoluteChange !== undefined ? `${isPositive ? '\u25B2' : '\u25BC'}${absoluteChange.toFixed(2)}` : ''

    const percentDisplay = `(${numericChangePercent >= 0 ? '+' : ''}${numericChangePercent.toFixed(2)}%)`

    const date = timestamp ? new Date(timestamp) : undefined
    let timestampDisplay = ''
    if (date && !isNaN(date.getTime())) {
      // Example target: Sep 15, 2025 3:28 PM
      try {
        timestampDisplay = format(date, 'MMM d, yyyy h:mm a')
      } catch {
        timestampDisplay = ''
      }
    }

    return { priceDisplay, changeDisplay, percentDisplay, isUp: isPositive, timestampDisplay }
  }, [stockSnapshot, loading, t])

  return (
    <ExternalLink
      href={paths.external.investorRelations.index}
      aria-label={t('investorRelations', 'Investor Relations')}
      className={classNames(
        'pl-4 flex flex-col items-start justify-center font-sans not-italic text-white pt-6 xl:pt-0 max-w-[320px]',
        className,
      )}
      onClick={() => track('ANGX Stock Ticker Click - Site Nav')}
    >
      <div className="flex flex-row items-baseline space-x-2 leading-none">
        <TitleXXS
          as={({ className, ...p }) => <span {...p} className={classNames(className, 'font-semibold tracking-wide')} />}
        >
          NYSE: ANGX
        </TitleXXS>
        <TitleXXS as={AsSpan} color="white">
          {priceDisplay}
        </TitleXXS>
        {changeDisplay && percentDisplay && (
          <CaptionMD as={AsSpan} color="white">
            {changeDisplay} {percentDisplay}
          </CaptionMD>
        )}
      </div>
      {timestampDisplay && (
        <CaptionSM
          as={({ className, ...p }) => <span {...p} className={classNames(className)} />}
          className="mt-2 block w-full xl:mt-0.5 xl:text-right"
          color="core-gray-400"
          weight="normal"
        >
          {timestampDisplay}
        </CaptionSM>
      )}
      <CaptionSM
        as={({ className, ...p }) => <span {...p} className={classNames(className)} />}
        className="mt-2 block w-full text-[13px] xl:hidden xl:text-right"
        color="core-gray-400"
        weight="normal"
      >
        You can now own Angel Stock and vote on the future of Angel.
      </CaptionSM>
    </ExternalLink>
  )
}
